import type { BaseRecord, DataProvider } from '@refinedev/core';
import type { AxiosInstance } from 'axios';

/**
 * Map of resource names to their known singular wrapper keys.
 * Explicit mapping avoids naive singularization bugs (e.g. "companies" → "companie")
 * and field-name collisions (e.g. an entity with an "order" field on /orders/:id).
 */
const RESOURCE_KEYS: Record<string, string> = {
  roles: 'role',
  users: 'user',
  permissions: 'permission',
};

/**
 * Unwrap a keyed response from the auth service.
 * Only unwraps if the key exists in our explicit map AND the value is a plain object.
 */
function unwrapSingular(
  payload: Record<string, unknown>,
  resource: string,
): Record<string, unknown> {
  const key = RESOURCE_KEYS[resource];
  if (key && payload[key] && typeof payload[key] === 'object' && !Array.isArray(payload[key])) {
    return payload[key] as Record<string, unknown>;
  }
  return payload;
}

/**
 * Resolve the API path for a given resource.
 * Supports `meta.apiPath` override so resources can be routed to arbitrary
 * backend paths (e.g. Refine resource `companies` → `admin/v1/companies`)
 * without coupling the Refine resource name to the URL segment.
 */
function resolvePath(resource: string, meta?: Record<string, unknown>): string {
  const override = meta?.apiPath;
  if (typeof override === 'string' && override.length > 0) {
    // Strip any leading slash — we always prefix with '/'.
    return override.replace(/^\/+/, '');
  }
  return resource;
}

/** Duck-typed so a signal from another realm (jsdom, an iframe) still counts. */
function isAbortSignal(value: unknown): value is AbortSignal {
  const v = value as AbortSignal | null | undefined;
  return !!v && typeof v.aborted === 'boolean' && typeof v.addEventListener === 'function';
}

/**
 * Creates a Refine data provider wrapping an axios client.
 * sf-api wraps most responses in { success: true, data: ... } — we unwrap .data.
 * The newer admin/v1/* surface (SF-3475) returns raw `{ data: [...], total, ... }`
 * envelopes for lists and raw entity objects for single-item reads; this provider
 * handles both shapes.
 */
export const createDataProvider = (client: AxiosInstance): DataProvider => ({
  getList: async ({ resource, pagination, filters, sorters, meta }) => {
    const params: Record<string, unknown> = {};

    // Pagination
    if (pagination && pagination.mode !== 'off') {
      const { current = 1, pageSize = 10 } = pagination;
      params.page = current;
      params.limit = pageSize;
    }

    // Sorting
    //
    // Most resources support a `-` prefix for descending order (e.g. `-name`).
    // Some API endpoints (e.g. admin/v1/companies) only accept plain field names
    // with no direction prefix. Callers opt-in to field-only sorting by setting
    // `meta.sortFieldOnly: true` — this keeps the data provider generic and avoids
    // fragile hardcoded path checks.
    if (sorters && sorters.length > 0) {
      if (meta?.sortFieldOnly === true) {
        // Field names only — no direction prefix (ascending-only endpoint).
        params.sort = sorters.map((s) => s.field).join(',');
      } else {
        // Standard: include `-` prefix for descending.
        params.sort = sorters
          .map((s) => (s.order === 'desc' ? `-${s.field}` : s.field))
          .join(',');
      }
    }

    // Filters
    if (filters) {
      filters.forEach((filter) => {
        if ('field' in filter) {
          params[filter.field] = filter.value;
        }
      });
    }

    const path = resolvePath(resource, meta);
    const { data: response } = await client.get(`/${path}`, { params });
    const payload = response.data ?? response;

    // Case 1: `data` field held the items array directly — the outer response is
    // the pagination envelope (admin/v1/* convention).
    if (Array.isArray(payload)) {
      return {
        data: payload,
        total: response.total ?? response.count ?? payload.length,
      };
    }

    // Case 2: legacy auth-service shape — { roles: [...] } / { users: [...] } etc.
    // Try the resource name as a key, then common pagination keys, then payload itself.
    const resourceData = payload[resource] ?? payload.items ?? payload.results ?? payload;
    const items = Array.isArray(resourceData) ? resourceData : [];

    return {
      data: items,
      total: payload.total ?? payload.count ?? items.length,
    };
  },

  getOne: async <TData extends BaseRecord = BaseRecord>({
    resource,
    id,
    meta,
  }: Parameters<NonNullable<DataProvider['getOne']>>[0]) => {
    const path = resolvePath(resource, meta);
    const { data: response } = await client.get(`/${path}/${id}`);
    const payload = response.data ?? response;
    // Auth service wraps responses: { role: {...} }, { user: {...} }
    // Only unwrap if the value is a plain object (not array/primitive) to avoid
    // collisions with entity fields that happen to match the singular name.
    const item = unwrapSingular(payload, resource);
    return { data: item as unknown as TData };
  },

  create: async ({ resource, variables, meta }) => {
    const path = resolvePath(resource, meta);
    const { data: response } = await client.post(`/${path}`, variables);
    const payload = response.data ?? response;
    return { data: payload };
  },

  update: async ({ resource, id, variables, meta }) => {
    const path = resolvePath(resource, meta);
    const { data: response } = await client.patch(`/${path}/${id}`, variables);
    const payload = response.data ?? response;
    return { data: payload };
  },

  deleteOne: async ({ resource, id, meta }) => {
    const path = resolvePath(resource, meta);
    const { data: response } = await client.delete(`/${path}/${id}`);
    const payload = response.data ?? response;
    return { data: payload };
  },

  getApiUrl: () => client.defaults.baseURL || '',

  /**
   * `meta.signal` (an AbortSignal) and `meta.timeout` (milliseconds, finite and above zero) are
   * optional and passed to axios only when given, so callers that send neither are unchanged.
   */
  custom: async ({ url, method = 'get', payload, headers, query, meta }) => {
    const { signal, timeout } = (meta ?? {}) as { signal?: unknown; timeout?: unknown };
    const requestConfig = {
      url,
      method,
      data: payload,
      headers: headers as Record<string, string>,
      params: query,
      ...(isAbortSignal(signal) ? { signal } : {}),
      ...(typeof timeout === 'number' && Number.isFinite(timeout) && timeout > 0
        ? { timeout }
        : {}),
    };
    const { data: response } = await client.request(requestConfig);
    const result = response.data ?? response;
    // sf-api paged lists send `{ success, data: [...], totalCount }`: keep the count as `total`
    // (as getList does) so the page can offer the rest. Every other answer is unchanged.
    const totalCount = (response as { totalCount?: unknown } | null)?.totalCount;
    return {
      data: result,
      ...(Array.isArray(result) && typeof totalCount === 'number' ? { total: totalCount } : {}),
    };
  },
});
